export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  SAFETY_STAFF = 'safety_staff',
  /** Internal id for "Administrador de Empresa" (full company admin). */
  COMPANY_ADMIN = 'company_admin',
  CLIENT_APPROVER = 'client_approver',
  /** Regular workspace user. With `User.manages.length > 0` they act as
   *  "Supervisor" (formerly client_team_supervisor) — UI surfaces team
   *  management when manages is non-empty. */
  CLIENT_STAFF = 'client_staff',
  SECURITY = 'security',
}

export enum RegistrationSource {
  NORMAL = 'normal',
  INVITATION_CODE = 'invitation_code',
  EMAIL_BATCH = 'email_batch'
}

export interface User {
  /** Derived in AuthContext from the ACTIVE company's entry: this session
   *  manages that company's work documents (see companies[].canManageWorkDocuments). */
  canManageWorkDocuments?: boolean;
  id?: string;
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  name?: string;
  phone?: string; // Added direct phone property
  nacionalidad?: string;
  cedula?: string;
  role: UserRole;
  /** Platform-wide role (super_admin / safety_staff). Mutually exclusive
   *  with the per-company roles in `companies[]`. Surfaces in some
   *  list/me responses; absent on cliente/proveedor users. */
  globalRole?: UserRole;
  /** Full set of platform roles this identity holds. When length > 1 the user
   *  can switch their active `globalRole` (super_admin <-> safety_staff) via
   *  AuthContext.switchGlobalRole without logging out. */
  globalRoles?: UserRole[];
  /** Break-glass account (env SUPER_SUPER_ADMIN_EMAIL). UI gating only. */
  isSuperSuperAdmin?: boolean;
  profilePicture?: string;
  position?: string;
  departments?: {
    _id: string;
    name: string;
    code?: string;
    description?: string;
    company?: {
      _id: string;
      name: string;
    };
  }[];
  company?: {
    _id: string;
    name: string;
    ruc?: string;
    address?: string;
    phone?: string;
    email?: string;
    isActive?: boolean;
    companyType?: 'cliente' | 'proveedor';
    settings?: {
      notificationDays?: number[];
      requiredCourses?: string[];
      modules?: Record<string, boolean>;
    };
  } | null;
  companyId?: string;
  /** Set only when the request was filtered by `?companyId=` — true when
   *  this user is the (active, non-external) company_admin of THAT
   *  specific company. Scoped to the queried company only; does not
   *  reveal admin status for any other company membership. */
  isAdminOfQueriedCompany?: boolean;
  companies?: {
    companyId: string;
    companyName: string;
    companyRuc?: string;
    role: UserRole;
    isActive: boolean;
    startDate: string;
    permissions: string[];
    isExternal?: boolean;
    /** Where the user actually works (their employer). Only set on external entries. */
    homeCompanyId?: string;
    homeCompanyName?: string;
    /**
     * The company that invited this user into THIS access link.
     * Equal to homeCompany on direct external invites; differs on transitive
     * invites (e.g., A pulled in a C-user via B → company=A, homeCompany=C, viaCompany=B).
     */
    viaCompanyId?: string;
    viaCompanyName?: string;
    /** Optionally populated by the BE — present when the endpoint populates `companies.company`. */
    company?: { _id: string; name: string; companyType?: 'cliente' | 'proveedor'; settings?: { modules?: Record<string, boolean> } };
    /** Only meaningful for the own (non-external) membership in a proveedor
     *  company — `false` means it has no active plan (see ProfileDrawer's
     *  "Cambiar de empresa" switcher). `undefined` for every other entry. */
    hasOwnActivePlan?: boolean;
    /** True on an EXTERNAL entry when the user is the admin of the proveedor
     *  company that lent them here: they manage this client's Fichas,
     *  Evaluaciones and Permisos like its own company_admin would. It is a
     *  capability, not a role — `role` stays client_staff. Set by the BE
     *  (providerWorkManagement.ts). */
    canManageWorkDocuments?: boolean;
    /** Populated object form of homeCompany/viaCompany — sent by getCompanyById instead of the flat *Id/*Name fields above. */
    homeCompany?: { _id: string; name: string } | string;
    viaCompany?: { _id: string; name: string } | string;
    joinedAt?: string;
    /** Set when this membership was removed ("Quitar de empresa") — the
     *  entry is kept for history, isActive flips to false. */
    removedAt?: string;
    removalReason?: string;
  }[];
  supervisedCompanies?: {
    _id: string;
    name: string;
  }[];
  /**
   * Direct reports — when populated, the holder acts as a "Supervisor"
   * (formerly the client_team_supervisor role; post-unification any
   * client_staff with non-empty manages takes on that capability via the
   * `"supervised"` data scope at the BE). Each entry is a user id (or
   * populated stub) listing whom this person manages. A staff member can
   * have many supervisors; query "who supervises me" with
   * `users?manages=<myId>` against the BE list endpoint.
   */
  manages?: Array<string | { _id: string; firstName?: string; lastName?: string; email?: string }>;
  isActive: boolean;
  active?: boolean; // Para compatibilidad
  status: 'active' | 'pending' | 'suspended' | 'deactivated';
  /** Platform-wide verification rollup recomputed by the BE on every
   *  review (`userVerificationStatusService.updateUserVerificationStatus`).
   *  Authoritative for "is this user verified" — replaces the
   *  retired `verificationData.verified` boolean. */
  verificationStatus?: 'unverified' | 'missing_documents' | 'pending_review' | 'rejected' | 'verified';
  acceptedTerms: boolean;
  acceptedPrivacyPolicy: boolean;
  verificationSummary?: {
    totalCompanies: number;
    compliantCompanies: number;
    partialCompanies: number;
    nonCompliantCompanies: number;
    details: {
      companyId: string;
      companyName: string;
      complianceStatus: 'compliant' | 'partial' | 'non_compliant';
      verificationsTotal: number;
      verificationsCompleted: number;
      verificationsPending: number;
      verificationsExpired: number;
    }[];
    globalCompliance: 'compliant' | 'partial' | 'non_compliant';
  };
  reviewSummary?: {
    receivedReviews: {
      total: number;
      averageRating: number;
      wouldHireAgainPercentage: number;
      recentReviews: any[];
    };
    givenReviews: {
      total: number;
      recentCount: number;
    };
    performance: {
      punctuality: number;
      quality: number;
      safety: number;
      communication: number;
      professionalBehavior: number;
    };
  };
  termsAcceptedAt: string;
  privacyPolicyAcceptedAt: string;
  profile?: {
    phone?: string;
    certifications?: string[];
    lastLogin?: string;
  };
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  /** Set when an admin invitation mints a single-use password-set token.
   *  Drives the "invitation expired / pending" state in admin views. */
  passwordResetExpires?: string;
  permitCount?: number;
  talentLMSUserId?: string;
  /** Pre-resolved profile picture URL (CDN/proxy). Falls back to `profilePicture` (storage key). */
  profilePictureUrl?: string;
  /** Aggregated per-user course counts surfaced by some list endpoints. */
  courseStats?: { totalEnrolled?: number; totalCompleted?: number };
  /** Per-user work permit list surfaced by some user detail endpoints. */
  permits?: Array<{
    _id: string;
    permitNumber?: string;
    status?: string;
    company?: string;
    /** Critical-task category label (trabajo_altura, etc.) when surfaced
     *  by the per-user permits aggregation. */
    category?: string;
  }>;

  __v?: number;
}

export interface CompanyCertification {
  name: string;
  issuedBy: string;
  issueDate: Date;
  expiryDate?: Date;
  status: 'valid' | 'expired' | 'pending';
}

export interface CompanyContactPerson {
  name: string;
  position: string;
  email: string;
  phone: string;
}

export interface CompanyContact {
  role: 'titular' | 'suplente_1' | 'suplente_2';
  name: string;
  email?: string;
  phone?: string;
}

export interface AccountExecutive {
  titular?: { _id: string; firstName: string; lastName: string; email: string; phone?: string } | string;
  suplente?: { _id: string; firstName: string; lastName: string; email: string; phone?: string } | string;
  /** Provenance of the titular assignment. Used to gate the "transferir cuenta"
   *  affordance — only seats originally set by super_admin can be transferred
   *  by the safety_staff member who holds them. */
  titularSource?: 'super_admin' | 'safety_staff' | 'self_signup';
  /** Set by safety_staff when they want super_admin to reassign the company. */
  reassignmentRequested?: boolean;
}

export interface CompanyLegalRepresentative {
  name: string;
  cedula: string;
  position: string;
}

export interface CompanyInsuranceInfo {
  provider: string;
  policyNumber: string;
  expiryDate: Date;
  coverage: string;
}

export interface CompanySettings {
  notificationDays: number[];
  requiredCourses: {
    name: string;
    isInitial: boolean;
  }[];
  modules?: {
    workPermits?: boolean;
    /** Hides the entire Marketplace tab/sidebar entry from the company. Defaults to true (enabled). */
    marketplace?: boolean;
    /** Hides Capacitaciones (cursos) from the company. Defaults to true (enabled). */
    capacitaciones?: boolean;
    /** When false, the company uses the platform default theme and the "Apariencia Visual" tab is disabled. Defaults to true. */
    customAppearance?: boolean;
  };
  /** Proveedor-only — controls the company's appearance in the marketplace. */
  marketplace?: {
    optedOut?: boolean;
    tags?: string[];
    tagline?: string;
    description?: string;
  };
  verifications?: {
    /** When false (default), provider verification rules can only be
     *  created at company-level (assigned to the provider's admin on
     *  behalf of the company) — the individual-per-worker level is hidden. */
    allowIndividualProviderVerifications?: boolean;
  };
}

export interface Company {
  id?: string;
  _id: string;
  name: string;
  taxId?: string;
  ruc?: string; // Para compatibilidad con API que devuelve ruc
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  industry?: string;
  companyType?: 'cliente' | 'proveedor';
  userCount?: number;
  serviceCardsInProgress?: number;
  contactPerson?: CompanyContactPerson;
  contacts?: CompanyContact[];
  accountExecutive?: AccountExecutive;
  legalRepresentative?: CompanyLegalRepresentative;
  insuranceInfo?: CompanyInsuranceInfo;
  certifications?: CompanyCertification[];
  status: 'active' | 'inactive' | 'suspended';
  settings?: CompanySettings;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  __v?: number;
  // Server-aggregated fields added by companyController.getCompanies to
  // each row of the companies list (not present on other Company-returning
  // endpoints) — see companyController.ts's `companiesWithData` mapping.
  logoUrl?: string | null;
  workPermitsInProgress?: number;
  /** Only set for companyType 'cliente' rows — capped name preview, see providerNamesTotal. */
  providerNames?: string[];
  providerNamesTotal?: number;
  providerCompaniesCount?: number;
  providerUsersCount?: number;
}

export interface CompanyFormData {
  name: string;
  taxId: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  industry: string;
  companyType?: 'cliente' | 'proveedor';
  userCount?: number;
  contactPerson: CompanyContactPerson;
  contacts?: CompanyContact[];
  accountExecutive?: {
    titular?: string | { _id: string; firstName?: string; lastName?: string; email?: string };
    suplente?: string | { _id: string; firstName?: string; lastName?: string; email?: string };
  };
  legalRepresentative: CompanyLegalRepresentative;
  insuranceInfo?: CompanyInsuranceInfo;
  certifications?: CompanyCertification[];
  status: 'active' | 'inactive' | 'suspended';
  settings?: CompanySettings;
}

// Re-exported from companyRelationshipApi — that was the fuller, actively
// maintained definition (allowProviderSelfService, invitedBy, requestedUsers
// sub-shape, etc.); this file used to carry a second, thinner duplicate that
// silently drifted out of sync with it.
export type { CompanyRelationship } from '../services/companyRelationshipApi';

export interface CompanyDashboardData {
  company: Company & { createdAt?: string; updatedAt?: string; employeeCount?: number; ruc?: string };
  users: {
    total: number;
    active: number;
    inactive: number;
    byRole: Record<string, number>;
  };
  verifications: {
    compliant: number;
    partial: number;
    nonCompliant: number;
    totalRequired: number;
    totalCompleted: number;
    verifiedPercentage: number;
  };
  serviceCards: {
    total: number;
    active: number;
    evaluating: number;
    closed: number;
    usersInActiveCards: number;
  };
  permits: {
    total: number;
    active: number;
    completed: number;
    cancelled: number;
  };
  courses: {
    averageCompletion: number;
    totalCompleted: number;
    totalEnrolled: number;
    courseList?: Array<{ name: string; enrolled: number; completed: number }>;
    requiredCourses?: Array<{ name: string; isInitial: boolean }>;
  };
  relationships?: {
    proveedores?: { active: number; inactive?: number; total?: number };
    clients?: { active: number; total?: number };
  };
  topUsers?: Array<{
    _id: string;
    name: string;
    email: string;
    role: string;
    isActive: boolean;
    isExternal?: boolean;
    homeCompany?: string;
    workPermitRating?: number;
  }>;
  verificationDetails?: Array<{
    _id: string;
    name: string;
    type: string;
    isRequired: boolean;
    completedCount: number;
    totalUsers: number;
  }>;
  serviceCardDetails?: Array<{
    _id: string;
    code: string;
    description: string;
    status: string;
    startDate: string;
    endDate: string;
    createdAt: string;
  }>;
  permitDetails?: Array<{
    _id: string;
    code: string;
    criticalTaskTypes: string[];
    description: string;
    location: string;
    status: string;
    startDate: string;
    endDate: string;
    createdAt: string;
  }>;
  attentionCalls?: { open: number; closed: number; total: number };
  incidents?: { open: number; closed: number; total: number };
  tickets?: { open: number; closed: number; total: number };
  qualityEvaluations?: Array<{
    providerId: string;
    providerName: string;
    count: number;
    avgAtencion: number;
    avgSeguridad: number;
    avgPuntualidad: number;
    avgTecnica: number;
    avgFlexibilidad: number;
    avgOverall: number;
    globalAvgOverall?: number;
    globalCount?: number;
  }>;
  fichaEvaluationDetails?: Array<{
    _id: string;
    code: string;
    description: string;
    status: string;
    providerId: string;
    providerName: string;
    atencionCliente: number;
    cumplimientoSeguridad: number;
    puntualidad: number;
    capacidadTecnica: number;
    flexibilidad: number;
    evaluatedBy?: { firstName: string; lastName: string };
    evaluatedAt?: string;
  }>;
  serviceCardsByProvider?: Array<{ providerId: string; providerName: string; total: number; active: number; closed: number }>;
  permitsByProvider?: Array<{ providerId: string; providerName: string; total: number; active: number; closed: number }>;
  providerBreakdown?: { total: number; empresas: number; independientes: number; list: Array<{ _id: string; name: string; type: string }> };
}

export interface Contractor {
  id: string;
  userId: string;
  fullName: string;
  cedula: string;
  ordenPatronal?: string;
  polizaINS?: string;
  status: 'active' | 'inactive';
  companyId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Course {
  id: string;
  contractorId: string;
  name: string;
  type: 'initial' | 'additional';
  completionDate: Date;
  expirationDate?: Date;
  certificateUrl?: string;
  status: 'valid' | 'expiring' | 'expired';
}

// TalentLMS specific types
export interface TalentLMSCourse {
  id: string;
  name: string;
  description: string;
  category: string;
  duration: number; // in minutes
  level: 'beginner' | 'intermediate' | 'advanced';
  price: number;
  currency: string;
  status: 'active' | 'inactive';
  created_on: string;
  last_update_on: string;
  thumbnail_url?: string;
  certification_enabled: boolean;
  required_for_companies?: string[]; // Company IDs that require this course
}

export interface TalentLMSUser {
  id: string;
  login: string;
  first_name: string;
  last_name: string;
  email: string;
  user_type: 'Learner' | 'Instructor' | 'Administrator';
  created_on: string;
  last_login?: string;
  avatar?: string;
  bio?: string;
  login_key?: string;
  deactivation_date?: string;
}

export interface TalentLMSEnrollment {
  id: string;
  user_id: string;
  course_id: string;
  enrollment_date: string;
  completion_status: 'not_started' | 'in_progress' | 'completed';
  completion_percentage: number;
  completion_date?: string;
  certificate_url?: string;
  time_spent: number; // in minutes
  last_accessed?: string;
  grade?: number;
}

export interface ContractorProgress {
  contractorId: string;
  talentLmsUserId?: string;
  enrollments: TalentLMSEnrollment[];
  totalCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  overallProgress: number;
  lastSyncDate: Date;
}

export interface CourseRequirement {
  id: string;
  companyId: string;
  courseId: string;
  courseName: string;
  isRequired: boolean;
  validityPeriod?: number; // in months
  jobRoles?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface TalentLMSWebhookEvent {
  event_type: 'user_signup' | 'user_login' | 'course_enrollment' | 'course_completion' | 'certificate_issued';
  user_id: string;
  course_id?: string;
  completion_percentage?: number;
  completion_date?: string;
  certificate_url?: string;
  timestamp: string;
}

export interface SyncOperation {
  id: string;
  type: 'contractor_sync' | 'bulk_sync' | 'course_sync' | 'progress_sync';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  startedAt: Date;
  completedAt?: Date;
  targetIds: string[]; // Contractor IDs or Course IDs
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  errors?: string[];
  initiatedBy: string; // User ID
}

// Approval Chain Entry interface for approval system
export type ApprovalChainEntryStatus = 'pending' | 'approved' | 'rejected' | 'delegated' | 'removed';

export interface ApprovalChainEntry {
  _id: string;
  approverId: string;
  approverName: string;
  approverEmail: string;
  approverRole?: string;
  approverDepartment?: string;
  isRequired: boolean;
  isBench: boolean;
  status: ApprovalChainEntryStatus;
  actionAt?: string;
  approvalDate?: string;
  comments?: string;
  notes?: string;
  // Rejection details
  rejectionReason?: string;
  rejectionCategory?: 'missing_info' | 'safety_concern' | 'incomplete_checklist' | 'other';
  // Signature
  signedBy?: string;
  signature?: string;
  // Delegation tracking
  delegatedFrom?: string;
  delegatedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  delegatedAt?: string;
  delegatedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  originalApprover?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  // Self-removal tracking
  removedAt?: string;
  removalReason?: string;
  // Added by admin tracking
  addedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  addedAt?: string;
}

// Approver type helper for UI display
export type ApproverType = 'obligatorio' | 'opcional' | 'suplente';

export const getApproverType = (entry: { isRequired: boolean; isBench: boolean }): ApproverType => {
  if (entry.isBench) return 'suplente';
  if (entry.isRequired) return 'obligatorio';
  return 'opcional';
};

export const getApproverTypeFromSelection = (type: ApproverType): { isRequired: boolean; isBench: boolean } => {
  switch (type) {
    case 'obligatorio':
      return { isRequired: true, isBench: false };
    case 'opcional':
      return { isRequired: false, isBench: false };
    case 'suplente':
      return { isRequired: false, isBench: true };
  }
};

export interface AssociatedForm {
  formId: string;
  formName: string;
  formCategory: string;
  addedAt: string;
  addedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  responses?: FormResponse[];
  status: 'pending' | 'in_progress' | 'completed';
  completedAt?: string;
  completedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
}

export interface FormResponse {
  fieldId: string;
  fieldName: string;
  fieldType: string;
  value: any;
  answeredAt: string;
  answeredBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
}

export interface Notification {
  id: string;
  userId: string;
  companyId?: string;
  type: 'course_expiring' | 'document_expiring' | 'permit_expiring' | 'system_alert' | 'work_reminder' | 'general';
  title: string;
  message: string;
  actionUrl?: string;
  read: boolean;
  readAt?: Date;
  createdAt: Date;
  updatedAt?: Date;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: Record<string, any>;
  emailSent?: boolean;
  emailSentAt?: Date;
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface NotificationFilters {
  type?: string[];
  priority?: string[];
  read?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  companyId?: string;
}

export interface PaginatedNotifications {
  notifications: Notification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BulkDeleteRequest {
  notificationIds: string[];
}

export interface CreateNotificationRequest {
  userId?: string;
  companyId?: string;
  type: Notification['type'];
  title: string;
  message: string;
  actionUrl?: string;
  priority?: Notification['priority'];
  metadata?: Record<string, any>;
  sendEmail?: boolean;
}

// Configuration and Settings Types

export interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  courseExpiring: boolean;
  documentExpiring: boolean;
  permitExpiring: boolean;
  workReminders: boolean;
  systemAlerts: boolean;
  dailyDigest: boolean;
}

export interface DashboardSettings {
  defaultView: 'overview' | 'detailed';
  refreshInterval: number; // in minutes
  showWelcomeMessage: boolean;
  compactMode: boolean;
  widgetOrder: string[];
  hideCompletedTasks: boolean;
}

export interface UserSettings {
  id: string;
  userId: string;
  theme: 'light' | 'dark' | 'auto';
  language: 'es' | 'en';
  timezone: string;
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  notifications: NotificationSettings;
  dashboard: DashboardSettings;
  updatedAt: Date;
}

export interface BrandingSettings {
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  companyName: string;
  favicon?: string;
  loginBackground?: string;
  customCSS?: string;
}

export interface PolicySettings {
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireSpecialChars: boolean;
  sessionTimeout: number; // in minutes
  maxLoginAttempts: number;
  lockoutDuration: number; // in minutes
  requireTwoFactor: boolean;
  allowRememberMe: boolean;
}

export interface FeatureSettings {
  enableContractors: boolean;
  enableWorkPermits: boolean;
  enableCourses: boolean;
  enableReports: boolean;
  enableNotifications: boolean;
  enableAuditLog: boolean;
  enableDataExport: boolean;
  maxFileUploadSize: number; // in MB
  allowedFileTypes: string[];
}

export interface CompanyAdminSettings {
  id: string;
  companyId: string;
  branding: BrandingSettings;
  policies: PolicySettings;
  features: FeatureSettings;
  contactInfo: {
    address: string;
    phone: string;
    email: string;
    website?: string;
  };
  businessHours: {
    monday: { start: string; end: string; enabled: boolean };
    tuesday: { start: string; end: string; enabled: boolean };
    wednesday: { start: string; end: string; enabled: boolean };
    thursday: { start: string; end: string; enabled: boolean };
    friday: { start: string; end: string; enabled: boolean };
    saturday: { start: string; end: string; enabled: boolean };
    sunday: { start: string; end: string; enabled: boolean };
  };
  updatedAt: Date;
}

export interface IntegrationSettings {
  emailProvider: {
    enabled: boolean;
    provider: 'smtp' | 'sendgrid' | 'ses';
    config: Record<string, any>;
  };
  smsProvider: {
    enabled: boolean;
    provider: 'twilio' | 'nexmo';
    config: Record<string, any>;
  };
  storageProvider: {
    provider: 'local' | 's3' | 'azure';
    config: Record<string, any>;
  };
  backupSchedule: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    retention: number; // in days
  };
}

export interface SystemSettings {
  id: string;
  security: {
    enableAuditLog: boolean;
    logRetention: number; // in days
    enableRateLimiting: boolean;
    allowConcurrentSessions: boolean;
    requireHttps: boolean;
  };
  integrations: IntegrationSettings;
  maintenance: {
    enableMaintenanceMode: boolean;
    maintenanceMessage: string;
    scheduledMaintenance?: {
      start: Date;
      end: Date;
      message: string;
    };
  };
  updatedAt: Date;
}

export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  resourceType: string;
  resourceId?: string;
  details: Record<string, any>;
  ipAddress: string;
  userAgent: string;
  timestamp: Date;
}

// Review Types
export interface Review {
  _id: string;
  contractor: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  reviewer: {
    _id: string;
    firstName: string;
    lastName: string;
    company: {
      _id: string;
      name: string;
    };
  };
  company: {
    _id: string;
    name: string;
  };
  rating: number;
  punctuality: number;
  quality: number;
  safety: number;
  communication: number;
  professionalBehavior: number;
  comment: string;
  wouldHireAgain: boolean;
  projectName?: string;
  workType?: string;
  tags?: string[];
  flagged?: {
    isFlagged: boolean;
    reason?: string;
    description?: string;
    flaggedBy?: string;
    flaggedAt?: string;
  };
  response?: {
    text: string;
    date: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateReviewInput {
  contractorId: string;
  rating: number;
  punctuality: number;
  quality: number;
  safety: number;
  communication: number;
  professionalBehavior: number;
  comment: string;
  wouldHireAgain: boolean;
  projectName?: string;
  workType?: string;
  tags?: string[];
}

export interface UpdateReviewInput {
  rating?: number;
  punctuality?: number;
  quality?: number;
  safety?: number;
  communication?: number;
  professionalBehavior?: number;
  comment?: string;
  wouldHireAgain?: boolean;
  projectName?: string;
  workType?: string;
  tags?: string[];
}

export interface ReviewSummary {
  totalReviews: number;
  averageRating: number;
  wouldHireAgainPercentage: number;
  metrics: {
    punctuality: number;
    quality: number;
    safety: number;
    communication: number;
    professionalBehavior: number;
  };
  ratingDistribution: {
    '1': number;
    '2': number;
    '3': number;
    '4': number;
    '5': number;
  };
}

export interface ReviewFilters {
  rating?: number;
  wouldHireAgain?: boolean;
  companyId?: string;
  dateFrom?: string;
  dateTo?: string;
  tags?: string[];
}

export interface FlagReviewInput {
  reason: string;
  description?: string;
}

// Theme Types - Simplified flat palette structure
export interface ThemePalette {
  default: string;
  primary: string;
  paper: string;
  sidebar: string;
  sidebartext: string;
  sidebarSelectedBg: string;
  sidebarSelectedText: string;
  buttonBg: string;
  buttonText: string;
  chipSuccess: string;
  chipWarning: string;
  chipError: string;
  chipInfo: string;
  tableRowHoverDefault: string;
  tableRowHoverError: string;
  tableRowHoverWarning: string;
}

export interface CompanyThemeSettings {
  lightTheme: ThemePalette;
  darkTheme: ThemePalette;
  logoUrl?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Service Management types (ServiceCard, InitialEvaluation, CriticalTaskPermit)
export * from './serviceManagement';